import * as React from 'react';
import { useRef, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, Checkbox, DatePicker, Dropdown, Input, Menu, Modal, Popover, Select } from 'antd';
import {
	ClockCircleOutlined,
	CloseOutlined,
	FileDoneOutlined,
	MoreOutlined,
	PlusOutlined,
	UserOutlined,
} from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { withUserType } from '../withUserType';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { EmptyState } from '../empty_state/EmptyState';
import useMountEffect from '../../hooks/useMountEffect';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import InlineEdit from '../ow_inline_todo_list/InlineEdit';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { runWithMessageFeedback } from '../../utils/MessageFeedbackUtils';
import { PROJECT_TASKS_CRUD_ACTION_CREATORS } from '../../actions/project_tasks_actions';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { cn } from '../../utils/ClassNamesUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { debounce } from '../../utils/PerformanceUtils';
import {
	projectTasksRestCrudThunksForBuyer,
	projectTasksRestCrudThunksForSupplier,
} from '../../thunks/project_tasks_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';
import {
	buyerContactsRestCrudThunksForBuyer,
	buyerContactsRestCrudThunksForSupplier,
} from '../../thunks/buyer_contacts_thunks';

require('./DeliverableTasks.less');

// A ProjectTask always belongs to a project (projectId) and MAY belong to a
// deliverable (deliverableId). Drives both the deliverable Tasks sub-tab
// (projectId + deliverableId) and the project Tasks tab (projectId only).
// Work-order association is intentionally NOT built — ProjectTask has no
// workOrderId field yet.
// Own contacts collection — the deliverable form's assignee picker uses its own
// (DELIVERABLE_FORM_CONTACTS_AUTOCOMPLETE) and both mount together inside the edit
// modal, so sharing one would let a task-assignee search overwrite the modal's list.
const CONTACTS_TC = 'PROJECT_TASK_CONTACTS_AUTOCOMPLETE';
const DELIVERABLES_TC = 'PROJECT_TASKS_DELIVERABLES';
const CONTACTS_PAGE_SIZE = 25;

const tcFor = (deliverableId) => (deliverableId ? 'DELIVERABLE_TASKS' : 'PROJECT_TASKS');

const contactName = (contact) => {
	const name = `${nullSafeGetOrElse('nameGiven', contact, '')} ${nullSafeGetOrElse(
		'nameFamily',
		contact,
		''
	)}`.trim();
	return name || nullSafeGetOrElse('email', contact, '');
};

// Full re-send on update; projectId/deliverableId numeric, dueDate ISO;
// completedAt is backend-managed. deliverableId is preserved unless overridden.
const toTaskPayload = (task, projectId, overrides: any = {}) => {
	const dueDate = nullSafeGet('dueDate', task);
	const deliverableId = nullSafeGet('deliverableId', task);
	return {
		id: nullSafeGet('id', task),
		projectId: Number(projectId),
		deliverableId: deliverableId != null ? Number(deliverableId) : undefined,
		title: nullSafeGet('title', task),
		assignedTo: nullSafeGet('assignedTo', task),
		dueDate: dueDate ? moment(dueDate).toISOString() : undefined,
		isCompleted: nullSafeGetOrElse('isCompleted', task, false),
		orderingId: nullSafeGet('orderingId', task),
		...overrides,
	};
};

// Backend supports only isCompleted + deliverableId filters (and id/createdAt
// sort). Those are the only filters surfaced via AdvancedFilters; assignee /
// deliverable-association filters and dueDate sort are backend gaps, so no inert
// controls are shown.
const STATUS_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'incomplete', label: 'Incomplete' },
	{ value: 'complete', label: 'Complete' },
];

// Static-options select used as an AdvancedFilters filterConfig `component`.
const StaticOptionsSelect = ({ value, onChange, items, placeholder, style }: any) => (
	<Select
		allowClear
		value={value}
		onChange={onChange}
		placeholder={placeholder}
		style={style || { width: '100%' }}
		options={items}
	/>
);

// Only-date quick presets (no time), matching the Figma "In 7d / In 1mo / In 3mo".
const QUICK_DATES = [
	{ label: 'In 7d', amount: 7, unit: 'days' },
	{ label: 'In 1mo', amount: 1, unit: 'months' },
	{ label: 'In 3mo', amount: 3, unit: 'months' },
];

// Rounded-full tag pill (Figma tag-pill): icon + label, optional remove x, and a
// dashed empty-state variant for unset Due/Assign affordances.
const TaskPill = ({ icon, children, onClick, onRemove, dashed = false, className = '' }: any) => (
	<span
		onClick={onClick}
		className={cn(
			'bodyregular inline-flex h-[26px] items-center gap-1.5 rounded-full border border-solid border-grey-5 bg-white px-2.5 text-character-primary',
			dashed && 'border-dashed text-character-secondary',
			onClick && 'cursor-pointer',
			className
		)}
	>
		{icon}
		{children}
		{onRemove ? (
			<CloseOutlined
				className="text-[10px] text-character-secondary"
				onClick={(event) => {
					event.stopPropagation();
					onRemove();
				}}
			/>
		) : null}
	</span>
);

// Due-date pill + popover: quick presets and a date-only calendar.
const DuePill = ({ value, onChange }: any) => {
	const [open, setOpen] = useState(false);
	const pick = (next) => {
		onChange(next || null);
		setOpen(false);
	};
	const content = (
		<div style={{ width: 280 }}>
			<div className="mb-2 flex gap-2">
				{QUICK_DATES.map((quick) => (
					<Button
						key={quick.label}
						size="small"
						onClick={() =>
							pick(
								moment()
									.add(quick.amount, quick.unit as any)
									.startOf('day')
							)
						}
					>
						{quick.label}
					</Button>
				))}
			</div>
			<DatePicker
				value={value}
				format="MMM D, YYYY"
				style={{ width: '100%' }}
				getPopupContainer={(trigger) => trigger.parentNode}
				onChange={(date) => pick(date ? date.startOf('day') : null)}
			/>
		</div>
	);
	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			<span className="inline-flex cursor-pointer">
				{value ? (
					<TaskPill
						icon={<ClockCircleOutlined className="text-[12px]" />}
						onRemove={() => onChange(null)}
					>
						{moment(value).format('MMM D')}
					</TaskPill>
				) : (
					<TaskPill icon={<ClockCircleOutlined className="text-[12px]" />} dashed>
						Due
					</TaskPill>
				)}
			</span>
		</Popover>
	);
};

// Assignee pill + popover: search + contact list (avatar / name / email).
const AssignPill = ({ email, contacts, onSearch, onChange }: any) => {
	const [open, setOpen] = useState(false);
	const pick = (next) => {
		onChange(next);
		setOpen(false);
	};
	const content = (
		<div style={{ width: 288 }}>
			<div className="footnoteregular mb-2 text-character-secondary">ASSIGN TO</div>
			<Input
				allowClear
				placeholder="input search text"
				className="mb-2"
				onChange={(event) => onSearch(event.target.value)}
			/>
			<div className="max-h-64 overflow-y-auto">
				<div
					className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2"
					onClick={() => pick(null)}
				>
					<span className="flex size-8 items-center justify-center rounded-full bg-grey-6 text-white">
						<UserOutlined />
					</span>
					<span className="bodyregular text-character-primary">No Assignee</span>
				</div>
				{(contacts || []).map((contact) => {
					const contactEmail = nullSafeGetOrElse('email', contact, '');
					return (
						<div
							key={contactEmail}
							onClick={() => pick(contactEmail)}
							className={cn(
								'flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2',
								contactEmail === email && 'bg-purple-1'
							)}
						>
							<ContactAvatar contact={contact} size="small" hidePopover />
							<div className="flex flex-col">
								<span className="bodyregular text-character-primary">{contactName(contact)}</span>
								<span className="footnoteregular text-character-secondary">{contactEmail}</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			<span className="inline-flex cursor-pointer">
				{email ? (
					<TaskPill onRemove={() => onChange(null)}>
						<UserNameWithEmail email={email} mode="inline" showNameWithAvatar size="small" />
					</TaskPill>
				) : (
					<TaskPill icon={<UserOutlined className="text-[12px]" />} dashed>
						Assign
					</TaskPill>
				)}
			</span>
		</Popover>
	);
};

const TaskRow = ({
	task,
	isProjectScope,
	deliverableName,
	contacts,
	onSearchContacts,
	onToggle,
	onSaveTitle,
	onChangeDue,
	onChangeAssignee,
	onRequestLinkDeliverable,
	onDelete,
}: any) => {
	const title = nullSafeGetOrElse('title', task, '');
	const isCompleted = nullSafeGetOrElse('isCompleted', task, false);
	const [editing, setEditing] = useState(false);

	// InlineEdit calls setValue on blur/Enter (its exit hook). For an actual
	// change, stay in edit mode until the save resolves — by then the store has
	// the new title, so dropping to the display text doesn't flash the old value
	// then the new one. No-op / empty edits exit immediately.
	const commitTitle = (value) => {
		const next = (value || '').trim();
		if (!next || next === title) {
			setEditing(false);
			return;
		}
		const result = onSaveTitle(task, next);
		if (result && result.finally) {
			result.finally(() => setEditing(false));
		} else {
			setEditing(false);
		}
	};

	const confirmDelete = () =>
		Modal.confirm({
			title: 'Delete this task?',
			okText: 'Delete',
			okButtonProps: { danger: true },
			cancelText: 'Cancel',
			onOk: () => onDelete(task),
		});

	const menu = (
		<Menu
			onClick={({ key }) => {
				if (key === 'link') onRequestLinkDeliverable(task);
				if (key === 'delete') confirmDelete();
			}}
			items={[
				...(isProjectScope ? [{ key: 'link', label: 'Link deliverable' }] : []),
				{ key: 'delete', label: 'Delete', danger: true },
			]}
		/>
	);

	return (
		<div className="border-0 border-b border-solid border-grey-4 py-3">
			<div className="flex items-start gap-2.5">
				<Checkbox checked={!!isCompleted} onChange={() => onToggle(task)} className="mt-1" />
				<div className="flex-1">
					{editing ? (
						<InlineEdit
							value={title}
							onEnter={() => {}}
							setValue={commitTitle}
							className={cn('bodyregular', isCompleted && 'text-character-disabled line-through')}
						/>
					) : (
						<div
							onClick={() => setEditing(true)}
							className={cn(
								'bodyregular cursor-text',
								isCompleted ? 'text-character-disabled line-through' : 'text-character-primary'
							)}
						>
							{title}
						</div>
					)}
				</div>
			</div>
			<div className="mt-2 flex flex-wrap items-center justify-end gap-2.5">
				{isProjectScope && nullSafeGet('deliverableId', task) ? (
					<TaskPill icon={<FileDoneOutlined className="text-[12px]" />}>{deliverableName}</TaskPill>
				) : null}
				<DuePill
					value={nullSafeGet('dueDate', task) ? moment(nullSafeGet('dueDate', task)) : null}
					onChange={(date) => onChangeDue(task, date)}
				/>
				<AssignPill
					email={nullSafeGet('assignedTo', task)}
					contacts={contacts}
					onSearch={onSearchContacts}
					onChange={(email) => onChangeAssignee(task, email)}
				/>
				<Dropdown trigger={['click']} overlay={menu} placement="bottomRight">
					<span className="inline-flex cursor-pointer">
						<TaskPill icon={<MoreOutlined className="text-[12px]" />} dashed />
					</span>
				</Dropdown>
			</div>
		</div>
	);
};

const AddTaskRow = ({ contacts, onSearchContacts, onCreate }: any) => {
	const [title, setTitle] = useState('');
	const [assignedTo, setAssignedTo] = useState(null);
	const [dueDate, setDueDate] = useState(null);

	const reset = () => {
		setTitle('');
		setAssignedTo(null);
		setDueDate(null);
	};

	const save = () => {
		const next = title.trim();
		if (!next) {
			return;
		}
		onCreate({ title: next, assignedTo, dueDate });
		reset();
	};

	return (
		<div className="border-0 border-b border-solid border-grey-4 bg-grey-2 py-3">
			<div className="flex items-center gap-2.5">
				<Checkbox disabled className="mt-0" />
				<Input
					autoFocus
					bordered={false}
					value={title}
					placeholder="Title"
					className="flex-1 px-0"
					onChange={(event) => setTitle(event.target.value)}
					onPressEnter={save}
				/>
				<div className="flex items-center gap-2.5">
					<DuePill value={dueDate} onChange={setDueDate} />
					<AssignPill
						email={assignedTo}
						contacts={contacts}
						onSearch={onSearchContacts}
						onChange={setAssignedTo}
					/>
					<OWButton type="primary" size="small" disabled={!title.trim()} onClick={save}>
						Add
					</OWButton>
				</div>
			</div>
		</div>
	);
};

const DeliverableTasks = ({
	projectId,
	deliverableId,
	contacts,
	deliverables,
	tasksSlice,
	createTask,
	updateTask,
	deleteTask,
	fetchTasksForList,
	fetchContacts,
	fetchDeliverables,
	refreshDeliverable,
	updateFilters,
	clearAndUpdateFilters,
}: any) => {
	const isProjectScope = deliverableId == null;
	const tc = tcFor(deliverableId);

	const [adding, setAdding] = useState(false);
	const [linkTask, setLinkTask] = useState(null);
	const [linkDeliverableId, setLinkDeliverableId] = useState(undefined);
	const [refreshNonce, setRefreshNonce] = useState(0);

	const debouncedContactSearch = useRef(debounce((term) => fetchContacts(term), 400, false));
	const onSearchContacts = (term) => debouncedContactSearch.current(term);

	// Base scope + a refresh nonce. PaginatedReduxTable passes this straight to the
	// fetch thunk (which strips the nonce); the deliverable/project scope is constant
	// per mount, the nonce changes only to force a post-mutation refetch.
	const baseScope = isProjectScope
		? { projectId: Number(projectId) }
		: { deliverableId: Number(deliverableId) };
	const additionalParams = { ...baseScope, _refresh: refreshNonce };

	useMountEffect(() => {
		// Reset any task filters persisted in the shared slice from a previous
		// project/deliverable, so opening this Tasks view doesn't carry a stale
		// deliverableId (which would send it with the new projectId and show nothing).
		clearAndUpdateFilters({}, tc);
		fetchContacts('');
		if (isProjectScope && projectId) {
			fetchDeliverables(projectId);
		}
	});

	// AdvancedFilters writes status / deliverableId into the project_tasks slice;
	// PaginatedReduxTable reads those slice filters and refetches on change, so the
	// wrappers only need to update the slice (translation to server params happens
	// in the fetch thunk).
	const applyUpdateFilters = (patch, targetCollectionName) => {
		updateFilters(patch, targetCollectionName);
	};
	const applyClearAndUpdateFilters = (patch, targetCollectionName) => {
		clearAndUpdateFilters(patch, targetCollectionName);
	};

	const refresh = () => {
		setRefreshNonce((nonce) => nonce + 1);
		if (deliverableId != null) {
			refreshDeliverable(deliverableId);
		}
	};

	const deliverableNameById = (id) => {
		const match = (deliverables || []).find((entry) => `${nullSafeGet('id', entry)}` === `${id}`);
		return match ? nullSafeGet('name', match) : `Deliverable #${id}`;
	};

	// Task writes aren't optimistic (the list only updates after the refetch), so wrap
	// each op in an antd message that transitions loading -> success/error.
	const runWithFeedback = (promise, loading, success, failure) =>
		runWithMessageFeedback(promise, { key: 'deliverable-task-op', loading, success, failure });

	const handleCreate = ({ title, assignedTo, dueDate }) => {
		const entity: any = {
			projectId: Number(projectId),
			isCompleted: false,
			title,
			assignedTo: assignedTo || undefined,
			dueDate: dueDate ? dueDate.startOf('day').toISOString() : undefined,
		};
		if (deliverableId != null) {
			entity.deliverableId = Number(deliverableId);
		}
		runWithFeedback(createTask(entity), 'Adding task…', 'Task added', 'Failed to add task').then(
			refresh
		);
	};

	const patchTask = (task, overrides, { loading, success, failure }) =>
		runWithFeedback(
			updateTask(toTaskPayload(task, projectId, overrides)),
			loading,
			success,
			failure
		).then(refresh);

	const handleToggle = (task) =>
		patchTask(
			task,
			{ isCompleted: !task.isCompleted },
			{
				loading: 'Updating task…',
				success: task.isCompleted ? 'Task marked incomplete' : 'Task completed',
				failure: 'Failed to update task',
			}
		);
	const handleSaveTitle = (task, title) =>
		patchTask(
			task,
			{ title },
			{
				loading: 'Saving…',
				success: 'Task updated',
				failure: 'Failed to update task',
			}
		);
	const handleChangeDue = (task, date) =>
		patchTask(
			task,
			{ dueDate: date ? date.startOf('day').toISOString() : null },
			{
				loading: 'Updating due date…',
				success: 'Due date updated',
				failure: 'Failed to update due date',
			}
		);
	const handleChangeAssignee = (task, email) =>
		patchTask(
			task,
			{ assignedTo: email == null ? null : email },
			{
				loading: 'Updating assignee…',
				success: 'Assignee updated',
				failure: 'Failed to update assignee',
			}
		);
	const handleDelete = (task) =>
		runWithFeedback(
			deleteTask(task),
			'Deleting task…',
			'Task deleted',
			'Failed to delete task'
		).then(refresh);

	const applyLinkDeliverable = () => {
		patchTask(
			linkTask,
			{ deliverableId: linkDeliverableId != null ? Number(linkDeliverableId) : null },
			{
				loading: 'Linking…',
				success: 'Deliverable linked',
				failure: 'Failed to link deliverable',
			}
		);
		setLinkTask(null);
		setLinkDeliverableId(undefined);
	};

	// PaginatedReduxTable owns the (server-paginated) rows; the header shows the
	// total count from the slice, not a cross-page completed/total fraction we can't
	// compute from a single page.
	const totalCount = nullSafeGetOrElse(`${tc}.count`, tasksSlice, 0);

	// Only backend-supported filters: status (isCompleted) and, in project scope,
	// deliverable (deliverableId).
	const filterConfig = [
		{
			label: 'Status',
			fieldName: 'status',
			placeholder: 'Status',
			component: StaticOptionsSelect,
			items: STATUS_OPTIONS,
			filtersValueAccessor: (slice) => nullSafeGet(`${tc}.filters.status`, slice),
			handleChange: (value) => applyUpdateFilters({ status: value }, tc),
		},
		...(isProjectScope
			? [
					{
						label: 'Deliverable',
						fieldName: 'deliverableId',
						placeholder: 'Deliverable',
						component: StaticOptionsSelect,
						items: (deliverables || []).map((entry) => ({
							value: nullSafeGet('id', entry),
							label: nullSafeGet('name', entry),
						})),
						filtersValueAccessor: (slice) => nullSafeGet(`${tc}.filters.deliverableId`, slice),
						handleChange: (value) => applyUpdateFilters({ deliverableId: value }, tc),
					},
			  ]
			: []),
	];

	return (
		<div>
			<div className="mb-3 flex items-center justify-between gap-3 border-0 border-b border-solid border-grey-4 pb-3">
				<span className="h5medium shrink-0 text-character-primary">Task List ({totalCount})</span>
				<div className="flex flex-1 items-center gap-3">
					<AdvancedFilters
						filtersTargetCollectionName={tc}
						filtersStateSlice={tasksSlice}
						clearAndUpdateFilters={applyClearAndUpdateFilters}
						updateFilters={applyUpdateFilters}
						filterConfig={filterConfig}
						showSearch={false}
					/>
				</div>
				<OWButton
					type="primary"
					size="large"
					icon={<PlusOutlined />}
					onClick={() => setAdding(true)}
				>
					Add Task
				</OWButton>
			</div>

			<div className="deliverable-tasks-table">
				<PaginatedReduxTable
					collectionName="project_tasks"
					targetCollectionName={tc}
					additionalParams={additionalParams}
					initialPagination={{ current: 1, pageSize: 20 }}
					initialFilters={{}}
					initialSorting={{ sort_by: 'createdAt', order: 'desc' }}
					showHeader={false}
					keyAccessor={(task) => task.id}
					rowClassName="deliverable-task-row"
					emptyState={
						<EmptyState headline="No tasks yet" body={<p>Add a task to track work here.</p>} />
					}
					fetchData={fetchTasksForList}
					columns={[
						{
							key: 'task',
							render: (_text, task) => (
								<TaskRow
									task={task}
									isProjectScope={isProjectScope}
									deliverableName={deliverableNameById(nullSafeGet('deliverableId', task))}
									contacts={contacts}
									onSearchContacts={onSearchContacts}
									onToggle={handleToggle}
									onSaveTitle={handleSaveTitle}
									onChangeDue={handleChangeDue}
									onChangeAssignee={handleChangeAssignee}
									onRequestLinkDeliverable={(clicked) => {
										setLinkTask(clicked);
										setLinkDeliverableId(nullSafeGet('deliverableId', clicked));
									}}
									onDelete={handleDelete}
								/>
							),
						},
					]}
				/>
			</div>
			{adding ? (
				<AddTaskRow
					contacts={contacts}
					onSearchContacts={onSearchContacts}
					onCreate={handleCreate}
				/>
			) : null}

			<Modal
				visible={linkTask != null}
				title="Link deliverable"
				okText="Link"
				onOk={applyLinkDeliverable}
				onCancel={() => setLinkTask(null)}
				destroyOnClose
			>
				<Select
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Select a deliverable"
					value={linkDeliverableId}
					onChange={(value) => setLinkDeliverableId(value)}
					style={{ width: '100%' }}
					options={(deliverables || []).map((entry) => ({
						value: nullSafeGet('id', entry),
						label: nullSafeGet('name', entry),
					}))}
				/>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	// Both TCs are registered in their slice's initial state, so the key is always
	// present and getRecordsForTargetCollection won't throw.
	contacts: getRecordsForTargetCollection(state.buyer_contacts, CONTACTS_TC),
	deliverables: getRecordsForTargetCollection(state.deliverables, DELIVERABLES_TC),
	tasksSlice: state.project_tasks,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
	const taskThunks = isSupplier
		? projectTasksRestCrudThunksForSupplier
		: projectTasksRestCrudThunksForBuyer;
	const deliverableThunks = isSupplier
		? deliverablesRestCrudThunksForSupplier
		: deliverablesRestCrudThunksForBuyer;
	const contactThunks = isSupplier
		? buyerContactsRestCrudThunksForSupplier
		: buyerContactsRestCrudThunksForBuyer;
	const tc = tcFor(ownProps.deliverableId);
	return {
		// PaginatedReduxTable dispatches this thunk itself, so return it (don't dispatch).
		// It receives (additionalParams, tc, pagination, sorting, sliceFilters): strip the
		// refresh nonce and translate the slice filters to the server params the backend
		// supports (isCompleted + deliverableId); createdAt-desc default (dueDate sort is a gap).
		fetchTasksForList: (params, targetCollectionName, pagination, sorting, filters) => {
			const nextParams: any = { ...(params || {}) };
			delete nextParams._refresh;
			const status = nullSafeGet('status', filters);
			if (status && status !== 'all') {
				nextParams.isCompleted = status === 'complete';
			}
			if (ownProps.deliverableId == null && nullSafeGet('deliverableId', filters)) {
				nextParams.deliverableId = Number(nullSafeGet('deliverableId', filters));
			}
			return taskThunks.read(
				nextParams,
				targetCollectionName,
				pagination,
				sorting || { sort_by: 'createdAt', order: 'desc' }
			);
		},
		createTask: (entity) => dispatch(taskThunks.create(entity, 'id', tc)),
		updateTask: (entity) => dispatch(taskThunks.update(entity)),
		// Immediate: the delete is already confirmed via Modal.confirm, so skip the
		// default 4s undo delay (which otherwise leaves the row on screen).
		deleteTask: (entity) => dispatch(taskThunks.deleteImmediate(entity)),
		refreshDeliverable: (id) => dispatch(deliverableThunks.readOne(id)),
		fetchContacts: (search) =>
			dispatch(contactThunks.read({ search, limit: CONTACTS_PAGE_SIZE, offset: 0 }, CONTACTS_TC)),
		fetchDeliverables: (projectId) =>
			dispatch(
				deliverableThunks.read({ projectId: Number(projectId) }, DELIVERABLES_TC, {
					current: 1,
					pageSize: 100,
				})
			),
		updateFilters: (updatedFilters, targetCollectionName) =>
			dispatch(
				PROJECT_TASKS_CRUD_ACTION_CREATORS.updateFilters(updatedFilters, targetCollectionName)
			),
		clearAndUpdateFilters: (updatedFilters, targetCollectionName) =>
			dispatch(
				PROJECT_TASKS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
					updatedFilters,
					targetCollectionName
				)
			),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(DeliverableTasks));
